import { privateClient } from "@/api/privateClient";
import { INSTITUTION_ADMIN_ENDPOINTS } from "../../../institutionAdmin.endpoints";
import type {
  InventoryItem,
  InventoryTransaction,
  InventoryIssueRecord,
  InventoryItemType,
  InventoryPenalty,
  InventoryLocationsResult,
  ReturnCondition,
  InventoryHistoryEntry,
  InventoryHistoryFilters,
  InventorySummary,
} from "../types";

const INVENTORY = INSTITUTION_ADMIN_ENDPOINTS.FACILITIES.INVENTORY;

export async function fetchItems(institutionId: number): Promise<InventoryItem[]> {
  const { data } = await privateClient.get(INVENTORY.ITEMS(institutionId));
  return data.items ?? [];
}

export async function createItem(
  institutionId: number,
  payload: { name: string; category?: string; unit?: string; itemType?: InventoryItemType; unitCost?: number }
): Promise<InventoryItem> {
  const { data } = await privateClient.post(INVENTORY.ITEMS(institutionId), payload);
  return data.item;
}

export async function recordTransaction(
  institutionId: number,
  payload: { itemId: number; direction: "IN" | "OUT"; quantity: number; reason?: string; referenceNo?: string }
): Promise<InventoryItem> {
  const { data } = await privateClient.post(INVENTORY.TRANSACTIONS(institutionId), payload);
  return data.item;
}

export async function fetchItemTransactions(institutionId: number, itemId: number): Promise<InventoryTransaction[]> {
  const { data } = await privateClient.get(INVENTORY.ITEM_TRANSACTIONS(institutionId, itemId));
  return data.transactions ?? [];
}

export async function issueToStudent(
  institutionId: number,
  payload: { itemId: number; studentId?: number; staffId?: number; quantity: number; academicYearId?: number; dueAt?: string; purpose?: string }
): Promise<InventoryIssueRecord> {
  const { data } = await privateClient.post(INVENTORY.ISSUE(institutionId), payload);
  return data.issue;
}

export async function fetchStudentIssues(
  institutionId: number,
  studentId: number,
  academicYearId?: number
): Promise<InventoryIssueRecord[]> {
  const { data } = await privateClient.get(INVENTORY.STUDENT_ISSUES(institutionId, studentId), { params: { academicYearId } });
  return data.issues ?? [];
}

export async function fetchIssues(
  institutionId: number,
  params?: { status?: "all" | "out" | "overdue"; itemId?: number }
): Promise<InventoryIssueRecord[]> {
  const { data } = await privateClient.get(INVENTORY.ISSUE(institutionId), { params });
  return data.issues ?? [];
}

export async function returnIssue(
  institutionId: number,
  issueId: number,
  payload: { quantity: number; condition: ReturnCondition; remarks?: string }
): Promise<{ issue: InventoryIssueRecord; penaltyCreated: boolean; penaltySkipped: boolean }> {
  const { data } = await privateClient.post(INVENTORY.ISSUE_RETURNS(institutionId, issueId), payload);
  return data;
}

export async function fetchPenalties(institutionId: number, status?: InventoryPenalty["status"]): Promise<InventoryPenalty[]> {
  const { data } = await privateClient.get(INVENTORY.PENALTIES(institutionId), { params: { status } });
  return data.penalties ?? [];
}

export async function chargePenalty(
  institutionId: number,
  penaltyId: number,
  payload: { feeComponentId: number; amount?: number; dueDate?: string; remarks?: string }
): Promise<InventoryPenalty> {
  const { data } = await privateClient.post(INVENTORY.PENALTY_ACTION(institutionId, penaltyId, "charge"), payload);
  return data.penalty;
}

export async function waivePenalty(institutionId: number, penaltyId: number, remarks: string): Promise<InventoryPenalty> {
  const { data } = await privateClient.post(INVENTORY.PENALTY_ACTION(institutionId, penaltyId, "waive"), { remarks });
  return data.penalty;
}

export async function reversePenalty(
  institutionId: number,
  penaltyId: number,
  payload: { remarks: string; itemRecovered?: boolean }
): Promise<InventoryPenalty> {
  const { data } = await privateClient.post(INVENTORY.PENALTY_ACTION(institutionId, penaltyId, "reverse"), payload);
  return data.penalty;
}

export async function fetchLocations(institutionId: number, itemId: number): Promise<InventoryLocationsResult> {
  const { data } = await privateClient.get(INVENTORY.ITEM_LOCATIONS(institutionId, itemId));
  return data;
}

export async function setLocation(
  institutionId: number,
  itemId: number,
  payload: { roomId?: number; totalQty: number; damagedQty?: number }
): Promise<InventoryLocationsResult> {
  const { data } = await privateClient.put(INVENTORY.ITEM_LOCATIONS(institutionId, itemId), payload);
  return data;
}

export async function changeCondition(
  institutionId: number,
  itemId: number,
  action: "damage" | "repair",
  payload: { roomId?: number; quantity: number }
): Promise<InventoryLocationsResult> {
  const { data } = await privateClient.post(INVENTORY.ITEM_CONDITION(institutionId, itemId, action), payload);
  return data;
}

export async function stocktake(
  institutionId: number,
  itemId: number,
  payload: { countedQuantity: number; reason: string }
): Promise<{ quantityOnHand: number; difference: number }> {
  const { data } = await privateClient.post(INVENTORY.ITEM_STOCKTAKE(institutionId, itemId), payload);
  return data;
}

export async function fetchHistory(
  institutionId: number,
  filters: InventoryHistoryFilters
): Promise<{ entries: InventoryHistoryEntry[]; pagination: { page: number; limit: number; total: number; totalPages: number } }> {
  const { source, ...rest } = filters;
  const { data } = await privateClient.get(INVENTORY.HISTORY(institutionId), { params: { ...rest, source: source?.length ? source.join(",") : undefined } });
  return data;
}

export async function fetchSummary(institutionId: number): Promise<InventorySummary> {
  const { data } = await privateClient.get(INVENTORY.SUMMARY(institutionId));
  return data;
}
